// import debug from "../debug";
import { Labels } from "../types";
import { BoardState } from "./events";
import { FunnelItem } from "./treeFunnel";

// const log = debug("funnel");

export type FunnelNode = {
    idx: number;
    label: string;
    count: number;
    weight: number;
    normalisedWeight: number;
    top: number;
    height: number;
    items: FunnelItem[];
};

export type Funnel = FunnelNode[];
export type FunnelInfo = {
    rescaledNodeHeight: boolean;
    zeroWeight: boolean;
    ignoredItems: number;
};

export const FIRST_NODE_LABEL = "__all__";

type FunnelParams = {
    centerVertically?: boolean;
    weightColumnId?: string;
    maxNodeHeightRatio?: number;
    calculateForIgnoredStatuses?: boolean;
};

export function funnel(
    board: BoardState,
    columnId: string,
    labels: Labels,
    trackedLabels: Set<string>,
    params: FunnelParams
): { funnel: Funnel; info: FunnelInfo } {
    const funnel: Funnel = [];
    const info: FunnelInfo = {
        rescaledNodeHeight: false,
        zeroWeight: false,
        ignoredItems: 0
    };

    const { weightColumnId, maxNodeHeightRatio, calculateForIgnoredStatuses } = params;
    const useMaxNodeHeightRatio = maxNodeHeightRatio || 1000;

    const labelIdToFunnelNode: { [labelId: string]: FunnelNode } = {};

    // prepend funnel with special node showing all items
    const zeroNode = {
        idx: 0,
        label: FIRST_NODE_LABEL,
        count: 0,
        weight: 0,
        normalisedWeight: 0,
        top: 0,
        height: 0,
        items: []
    } as FunnelNode;

    labels.orderedIds.forEach((labelId, idx) => {
        if (calculateForIgnoredStatuses || trackedLabels.has(`${labelId}`)) {
            const funnelNode = {
                idx,
                label: labelId,
                count: 0,
                weight: 0,
                normalisedWeight: 0,
                top: 0,
                height: 0,
                items: []
            };
            funnel.push(funnelNode);
            labelIdToFunnelNode[labelId] = funnelNode;
        }
    });

    if (funnel.length === 0) {
        return { funnel, info };
    }

    Object.keys(board.items).forEach((itemId) => {
        const item = board.items[itemId];
        const labelId = item[columnId];
        const weight = weightColumnId ? item[weightColumnId] : 1;
        const weightNumber = weight !== undefined && weight !== "" && !isNaN(weight) ? parseFloat(weight) : 1;

        if (labelId !== undefined && (calculateForIgnoredStatuses || trackedLabels.has(`${labelId}`))) {
            const funnelNode = labelIdToFunnelNode[labelId];
            const idx = labels.order[labelId];
            funnelNode.count += 1;
            funnelNode.weight += weightNumber;
            funnelNode.items.push({
                id: itemId,
                visitedLabels: labels.orderedIds.slice(0, idx + 1),
                visitedLevels: Array(idx + 1)
                    .fill(undefined)
                    .map((_, idx) => idx)
            });
        } else {
            info.ignoredItems += 1;
            zeroNode.count += 1;
            zeroNode.weight += weightNumber;
            zeroNode.items.push({
                id: itemId,
                visitedLabels: [],
                visitedLevels: []
            });
        }
    });

    if (zeroNode.count > 0) {
        funnel.unshift(zeroNode);
        funnel.forEach((n, i) => n.idx = i);
    }

    // sum counts assuming reaching label X required item to go through all labels 0..X-1
    let sum = 0;
    let weights = 0;
    for (let i = funnel.length - 1; i >= 0; i--) {
        let temp = funnel[i].count;
        funnel[i].count += sum;
        sum += temp;

        temp = funnel[i].weight;
        funnel[i].weight += weights;
        weights += temp;
    }

    //
    // normalise weight
    //
    const maxWeight = funnel[0].weight;
    const nonEmptyFunnel = funnel.filter((node) => node.count > 0);
    const nonZeroWeights = nonEmptyFunnel.filter((node) => node.weight !== 0).map((node) => node.weight);
    const minWeight = nonZeroWeights && nonZeroWeights.length > 0 ? Math.min(...nonZeroWeights) : 0;

    if (minWeight === 0 && maxWeight === 0) {
        info.zeroWeight = true;
        return { funnel, info };
    }

    const ratio = minWeight === 0 ? maxWeight : maxWeight / minWeight;
    let normalisationFunc: (weight: number) => number;

    if (ratio > useMaxNodeHeightRatio) {
        info.rescaledNodeHeight = true;
        const newMaxWeight = minWeight * useMaxNodeHeightRatio;

        normalisationFunc = (weight: number): number =>
            minWeight + (weight - minWeight) * ((newMaxWeight - minWeight) / (maxWeight - minWeight));
    } else {
        normalisationFunc = (weight): number => weight;
    }

    nonEmptyFunnel.forEach(
        (node) => (node.normalisedWeight = normalisationFunc(node.weight || maxWeight / useMaxNodeHeightRatio))
    );

    //
    // calculate top and height normalised to <0..1>
    //
    const maxNormalisedWeight = funnel[0].normalisedWeight;
    if (maxNormalisedWeight === 0) {
        nonEmptyFunnel.forEach((node) => {
            node.height = node.count > 0 ? 1.0 : 0.0;
            node.top = 0.0;
        });
    } else {
        nonEmptyFunnel.forEach((node) => {
            node.height = 1.0 - (maxNormalisedWeight - node.normalisedWeight) / maxNormalisedWeight;
            node.top = (1.0 - node.height) * 0.5;
        });
    }

    return { funnel, info };
}
